import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

import './index.css';

import environment from '@config/environment';
import { registerCallerHeader } from '@/api/callerHeader';
import {
  hasPersistedCancerDetectContext,
  hasPersistedPatientContext,
  shutdownFullStory,
} from '@utils/fullstory';
import App from './App';

Sentry.init({
  dsn: environment.SENTRY_DSN,
  // Route envelopes through a same-origin path so ad blockers (Brave Shields,
  // uBlock) can't match them against *.sentry.io block lists — nginx (prod) and
  // the vite dev proxy forward /mon to the Sentry ingest endpoint.
  tunnel: '/mon',
  environment: environment.ENVIRONMENT,
  release: environment.SENTRY_RELEASE_ID,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: parseFloat(environment.SENTRY_TRACES_SAMPLE_RATE), //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  enableLogs: true,
});

// FullStory initialization
window['_fs_host'] = 'an-signals.viome.com';
window['_fs_script'] = 'an-signals.viome.com/s/fs.js';
window['_fs_app_host'] = 'app.fullstory.com';
window['_fs_org'] = 'o-24J9BR-na1';
window['_fs_namespace'] = 'FS';
/* eslint-disable */
!function (m: any, n: any, e: any, t: any, l: any, o: any, g: any, y: any) {
  var s, f, a = function (h: any) {
    return !(h in m) || (m.console && m.console.log && m.console.log ('FullStory namespace conflict. Please set window["_fs_namespace"].'), !1)
  } (e);
  function p (b: any) {
    var h: any, d: any[] = [];
    function j () {
      h && (d.forEach ((function (b) {
        var d;
        try { d = b[h[0]] && b[h[0]] (h[1]) } catch (h) { return void (b[3] && b[3] (h)) }
        d && d.then ? d.then (b[2], b[3]) : b[2] && b[2] (d)
      })), d.length = 0)
    }
    function r (b: any) { return function (d: any) { h || (h = [b, d], j ()) } }
    return b (r (0), r (1)), { then: function (b: any, h: any) { return p ((function (r: any, i: any) { d.push ([b, h, r, i]); j () })) } }
  }
  a && (g = m[e] = function () {
    var b = function (b: any, d: any, j: any, r: any) {
      function i (i: any, c: any) { h (b, d, j, i, c, r) }
      r = r || 2;
      var c, u = /Async$/;
      return u.test (b) ? (b = b.replace (u, ''), 'function' == typeof Promise ? new Promise (i) : p (i)) : h (b, d, j, c, c, r)
    };
    function h (h: any, d: any, j: any, r: any, i: any, c: any) { return b._api ? b._api (h, d, j, r, i, c) : (b.q && b.q.push ([h, d, j, r, i, c]), null) }
    return b.q = [], b
  } (), y = function (b: any) {
    function h (h: any) { 'function' == typeof h[4] && h[4] (new Error (b)) }
    var d = g.q;
    if (d) { for (var j = 0; j < d.length; j++) h (d[j]); d.length = 0; d.push = h }
  }, function () {
    (o = n.createElement (t)).async = !0;
    o.crossOrigin = 'anonymous';
    o.src = 'https://' + l;
    o.onerror = function () { y ('Error loading ' + l) };
    var b = n.getElementsByTagName (t)[0];
    b && b.parentNode ? b.parentNode.insertBefore (o, b) : n.head.appendChild (o)
  } (), function () {
    function b () { }
    function h (b: any, h: any, d: any) { g (b, h, d, 1) }
    function d (b: any, d: any, j: any) { h ('setProperties', { type: b, properties: d }, j) }
    function j (b: any, h: any) { d ('user', b, h) }
    function r (b: any, h: any, d: any) { j ({ uid: b }, d); h && j (h, d) }
    g.identify = r; g.setUserVars = j; g.identifyAccount = b; g.clearUserCookie = b; g.setVars = d;
    g.event = function (b: any, d: any, j: any) { h ('trackEvent', { name: b, properties: d }, j) };
    g.anonymize = function () { r (!1) };
    g.shutdown = function () { h ('shutdown') };
    g.restart = function () { h ('restart') };
    g.log = function (b: any, d: any) { h ('log', { level: b, msg: d }) };
    g.consent = function (b: any) { h ('setIdentity', { consent: !arguments.length || b }) }
  } (), s = 'fetch', f = 'XMLHttpRequest', g._w = {}, g._w[f] = m[f], g._w[s] = m[s],
  m[s] && (m[s] = function () { return g._w[s].apply (this, arguments) }), g._v = '2.0.0')
} (window, document, (window as any)['_fs_namespace'], 'script', (window as any)['_fs_script'], null, null, null);
/* eslint-enable */

// Shutdown does not persist across a full reload, so re-apply it immediately
// for a persisted PRO or CancerDetect session — before React mounts, and
// before FullStory's own script has even finished loading (the call queues
// and applies once it does), so those reloads are never captured, even briefly.
if (hasPersistedPatientContext() || hasPersistedCancerDetectContext()) {
  shutdownFullStory();
}

(window as any).FS?.setUserVars?.({ environment: environment.ENVIRONMENT, source_app: 'viome_web_app' });

const setSentryUser = ({ userId, name, email }: { userId: string; name: string; email: string; }) => {
  Sentry.setUser({
    id: userId,
    userName: name,
    userEmail: email,
  });
};

registerCallerHeader();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

export { Sentry, setSentryUser };
